import { useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { IOrganizationGetOne } from '@frontend/components/interfaces/organizationGetOne';
import { ManagerAuthorization } from '@common/enums';
import { Loading } from '@frontend/components/elements/common';
import { SideNavigationTemplate } from '@frontend/components/templates/SideNavigationTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';
import { ServiceOrganizationSettingsList } from '../serviceOrganizationSettings/ServiceOrganizationSettingsList';
import { OrganizationBlock } from './OrganizationBlock';
import { OrganizationPractitionersList } from './OrganizationPractitionersList';
import { OrganizationServicesConsumingList } from './OrganizationServicesConsumingList';
import { OrganizationServicesProvidingList } from './OrganizationServicesProvidingList';

interface ITab {
    content: JSX.Element;
    name: string;
    title: string;
}

export function OrganizationAdminDetails() {
    const { id } = useParams();
    const [organization, setOrganization] = useState<IOrganizationGetOne | undefined>(undefined);

    useEffect(() => {
        if (id) {
            loadOrganizationAdminDetails(id);
        }
    }, [id]);

    const tabs: ITab[] = [];

    if (organization && hasOneAuthorization(ManagerAuthorization.employmentsOld)) {
        tabs.push({
            content: <OrganizationPractitionersList organizationId={organization.id} />,
            name: 'practitioners',
            title: translateKey('practitioner.practitioners'),
        });
    }
    if (organization && hasOneAuthorization(ManagerAuthorization.serviceRightsCareproviders)) {
        tabs.push(
            {
                content: <OrganizationServicesProvidingList id={organization.id} />,
                name: 'providers',
                title: translateKey('practitioner.providingServices'),
            },
            {
                content: <OrganizationServicesConsumingList id={organization.id} />,
                name: 'consumers',
                title: translateKey('practitioner.consumingServices'),
            }
        );
    }

    if (organization && hasOneAuthorization(ManagerAuthorization.serviceRightsOrganizations)) {
        tabs.push({
            content: (
                <ServiceOrganizationSettingsList
                    filterId={organization.id}
                    filterType="organization"
                />
            ),
            name: 'serviceOrganizationSettings',
            title: translateKey('serviceProvider.serviceOrganizationSettings'),
        });
    }

    return (
        <Loading
            item={organization}
            loaded={(org) => (
                <SideNavigationTemplate
                    breadcrumb={[{ text: translateKey('organization.organizations'), url: '/organizations' }]}
                    infoBlock={<OrganizationBlock organization={org} />}
                    tabs={tabs}
                />
            )}
        />
    );

    async function loadOrganizationAdminDetails(organizationId: string) {
        const url = `/api/organizations/${organizationId}`;
        const result = await fetchJson(url, 'GET');
        setOrganization(result.json);
    }
}
